/**
 * KpiStrip — the KPI row at the top of a pillar page, as Carbon tiles in a
 * condensed grid (1px gutters; the Gutter Rule). Each tile: the label as the
 * tile title, a 3.5rem light figure, the one-sentence stat, an optional
 * helper trend line, and the page's sparkline pinned to the foot.
 *
 * Severity follows the Neutral Bad State Rule: `warn` is neutral like
 * `tone`, and `crit` leads the label with the critical octagon (shape and
 * Coral), never a colored numeral. Three tiles across from 672px, stacked on
 * phones.
 */

import type { PillarTone } from '@/components/Dashboard/v2/PillarShell'
import { BigNumber } from '@/components/carbon/BigNumber'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { Marked } from '@/components/carbon/Marked'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { Tile } from '@/components/carbon/Tile'

/** Severity hint for a single KPI. `tone` (default) and `warn` render
 *  neutral; `crit` adds the critical glyph to the label. */
export type KpiSeverity = 'tone' | 'warn' | 'crit'

export interface KpiCell {
  /** Stable key per-KPI; helps React diffing if the page swaps cells. */
  id: string
  /** The figure. Pre-formatted by the page (locale and zero-pad rules vary). */
  numeral: string
  /** Optional unit after the figure (e.g. "%"). */
  numeralSuffix?: string
  /** The tile title, e.g. "Active drifts". */
  label: string
  /** One-sentence stat below the figure. Plain prose, kept short. */
  stat: string
  /** Optional trend line; `**bold**` runs render in primary ink. */
  trend?: string
  /** Optional sparkline node, typically a recharts <ResponsiveContainer />.
   *  The page owns the data; the tile gives it a 36px slot at its foot. */
  spark?: React.ReactNode
  /** Severity hint. Defaults to neutral. */
  severity?: KpiSeverity
}

interface Props {
  /** The pillar. Kept for the frozen interface; the tiles are neutral. */
  tone: PillarTone
  /** Normally three cells. */
  cells: ReadonlyArray<KpiCell>
}

type Cols = 1 | 2 | 3 | 4

export default function KpiStrip({ cells }: Props) {
  const n = Math.min(Math.max(cells.length, 1), 4) as Cols
  const columns = n === 4 ? { base: 1 as const, 'c-md': 2 as const, 'c-xlg': 4 as const } : { base: 1 as const, 'c-md': n }

  return (
    <CondensedGrid as="section" aria-label="Key figures" columns={columns} className="mb-8">
      {cells.map(cell => (
        <Tile key={cell.id} className="flex min-w-0 flex-col">
          <h3 className="m-0 text-body font-semibold leading-[1.29] text-text-primary">
            {cell.severity === 'crit' ? <SeverityGlyph level="crit">{cell.label}</SeverityGlyph> : cell.label}
          </h3>
          <BigNumber value={cell.numeral} suffix={cell.numeralSuffix} size="figure-lg" className="mt-5" />
          <p className="m-0 mt-3 text-text-secondary">{cell.stat}</p>
          {cell.trend ? (
            <p className="m-0 mt-2 text-helper text-text-helper">
              <Marked text={cell.trend} />
            </p>
          ) : null}
          {cell.spark ? (
            <div aria-hidden="true" className="mt-auto w-full pt-4">
              <div className="h-9 w-full">{cell.spark}</div>
            </div>
          ) : null}
        </Tile>
      ))}
    </CondensedGrid>
  )
}
